import { useState, useMemo, useEffect } from "react";
import { useHighlightTransactionFromUrl } from "@/hooks/useHighlightTransactionFromUrl";
import { useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { Transaction, TransactionFilters as TransactionFiltersType } from "@/types/payments";
import TransactionsTable from "@/components/cash/TransactionsTable";
import TransactionFilters from "@/components/cash/TransactionFilters";
import { TransactionListFooter, TransactionStatusTabs } from "@/components/bank/TransactionListParts";
import BankAccountMultiSelect from "@/components/cash/BankAccountMultiSelect";
import { TransactionMatchDialogs } from "@/components/cash/TransactionMatchDialogs";
import { useTransactionDialogs } from "@/hooks/useTransactionDialogs";
import { formatCurrency } from "@/utils/paymentUtils";
import { useBankAccounts } from "@/hooks/useBankAccounts";
import { useTransactionFilter } from "@/hooks/useTransactionFilter";
import { useTransactionLoader } from "@/hooks/useTransactionLoader";
import { useTransactionMatching } from "@/hooks/useTransactionMatching";

const Transactions = () => {
  const location = useLocation();
  const t = useTranslations('consultantTransactions.page');
  // Transactions (and loading/error) are now owned by the loader — it reads a paginated page
  // from the DB via TanStack Query (issue #363).
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState<keyof Transaction | null>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const dialogs = useTransactionDialogs();
  const [selectedTab, setSelectedTab] = useState("all");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  
  // Get accountId and transactionId from URL query parameters if present
  const searchParams = new URLSearchParams(location.search);
  const accountIdFromUrl = searchParams.get('accountId');
  const transactionIdFromUrl = searchParams.get('id');
  
  const [filters, setFilters] = useState<TransactionFiltersType>({
    matched: 'all',
    description: '',
    minAmount: '',
    maxAmount: '',
    startDate: '',
    endDate: '',
  });

  // The company's real accounts feed the account filter (FIN-802); each transaction row carries
  // its own account label, so the list does not depend on this load succeeding.
  const { accounts, accountsError } = useBankAccounts();

  const {
    transactions: loaderTransactions,
    setTransactions: setLoaderTransactions,
    loadingTransactions,
    transactionsError,
    page,
    pageSize,
    total,
    setPage,
  } = useTransactionLoader(selectedAccounts, filters);

  // Transaction matching (issue #497) — reuses the same hook Bank.tsx's useTransactions chain
  // uses, so the receivable-confirm path actually persists via manualMatchService.createManualMatch
  // instead of the previous local-state-only no-op, and the new payable-confirm path reuses
  // #462's PaymentService.confirmPayableMatch end-to-end.
  const {
    setSelectedTransactionId,
    selectedTransaction,
    handleManualMatch: handleSelectTransactionForMatch,
    handleMatchSelection,
    handleConfirmPayableMatch,
    handleConfirmOwnAccountMatch
  } = useTransactionMatching(loaderTransactions, setLoaderTransactions);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Selecting/deselecting accounts changes the server query (single account, a server-side
  // subset, or every account), so reset back to the first page to avoid landing past the end of
  // the new result set.
  const handleSelectedAccountsChange = (accounts: string[]) => {
    setSelectedAccounts(accounts);
    setPage(1);
  };

  // Update selected accounts if accountId in URL changes. The loader's query reacts to the
  // selection change and fetches the account-scoped page automatically.
  useEffect(() => {
    if (accountIdFromUrl) {
      setSelectedAccounts([accountIdFromUrl]);
      setPage(1);
    }
  }, [accountIdFromUrl, setPage]);

  // Handle URL parameter for specific transaction
  useHighlightTransactionFromUrl(transactionIdFromUrl, loaderTransactions, setSelectedTab);

  // Filter the loaded page by tab/matched status (issue #363 review follow-up: the account
  // selection and description/amount/date filters are pushed down to the DB query by
  // useTransactionLoader). `matched` stays page-scoped because it's derived per-row, not a
  // stored column.
  const filteredTransactions = useTransactionFilter(loaderTransactions, filters, selectedTab);

  // Sort transactions based on sort key and direction
  const sortedTransactions = useMemo(() => {
    if (!sortKey) return filteredTransactions;
    
    return [...filteredTransactions].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (a[sortKey] > b[sortKey]) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredTransactions, sortKey, sortDirection]);

  // Handle sorting when a column header is clicked
  const handleSort = (key: keyof Transaction) => {
    setSortDirection(prevDirection => 
      sortKey === key && prevDirection === 'asc' ? 'desc' : 'asc'
    );
    setSortKey(key);
  };

  // Render sort icon
  const renderSortIcon = (key: keyof Transaction) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof TransactionFiltersType, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      matched: 'all',
      description: '',
      minAmount: '',
      maxAmount: '',
      startDate: '',
      endDate: '',
    });
    setSelectedAccounts([]);
  };

  // Handle manual matching — opens the modal on top of the hook's selection state
  const handleManualMatch = (transactionId: string) => {
    handleSelectTransactionForMatch(transactionId);
    dialogs.setMatchOpen(true);
  };

  // Handle mark as
  const handleMarkAs = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    dialogs.setMarkAsOpen(true);
  };

  if (loadingTransactions) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-xl">{t('loading')}</span>
      </div>
    );
  }

  if (transactionsError) {
    return (
      <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
        <p className="text-red-600 mb-4">{transactionsError}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
        >
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold tracking-tight">{t('title')}</h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('allTitle')}</CardTitle>
          <CardDescription>
            {t('description')}
          </CardDescription>
          
          {/* Bank Account Filter is now above the tabs */}
          <div className="mt-4">
            <BankAccountMultiSelect
              accounts={accounts}
              loadFailed={!!accountsError}
              selectedAccounts={selectedAccounts}
              setSelectedAccounts={handleSelectedAccountsChange}
            />
          </div>
        </CardHeader>
        <CardContent>
          <TransactionStatusTabs selectedTab={selectedTab} onSelectedTabChange={setSelectedTab} />
          
          <div className="flex justify-end mb-4">
            <TransactionFilters 
              filters={filters}
              handleFilterChange={handleFilterChange}
              resetFilters={resetFilters}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          </div>
          
          <TransactionsTable
            transactions={sortedTransactions}
            formatCurrency={(amount, currency) => formatCurrency(amount, currency ?? 'USD')}
            handleManualMatch={handleManualMatch}
            handleMarkAs={handleMarkAs}
            sortKey={sortKey}
            sortDirection={sortDirection}
            handleSort={handleSort}
            renderSortIcon={renderSortIcon}
            highlightTransactionId={transactionIdFromUrl}
          />
          
          <TransactionListFooter
            shownCount={sortedTransactions.length}
            loadedCount={loaderTransactions.length}
            total={total}
            selectedAccountCount={selectedAccounts.length}
            selectedTab={selectedTab}
            matchedFilter={filters.matched}
            page={page}
            totalPages={totalPages}
            setPage={setPage}
          />
        </CardContent>
      </Card>
      
      <TransactionMatchDialogs
        transaction={selectedTransaction}
        dialogs={dialogs}
        matching={{ handleMatchSelection, handleConfirmPayableMatch, handleConfirmOwnAccountMatch }}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default Transactions;
